import React, { useEffect, useRef, useState, useContext } from "react";

// Type for items in the watchList
interface WatchListItem {
  scripId: string;
  stockName: string;
  stockId?: string;
  active?: string;
}
import { Link, useHistory } from "react-router-dom";
import { ToastContainer, toast, Bounce } from "react-toastify";
import { Container } from "../components/styled.ts";
import { Typography } from "../components/typography/index.tsx";
import { useMobile } from "../hooks/isMobile.tsx";
import { Search } from "../components/search/index.tsx";
import { Pill } from "../components/pill/index.tsx";
import { Tile } from "../components/tile/index.tsx";
import { SearchResults } from "../components/SearchResults/index.tsx";
import { Header } from "../components/header/index.tsx";
import { Seo } from "../components/seo/index.tsx";
import { Filter } from "../components/filter/index.tsx";
import { subscriberContext } from "../context/subscriberContext.tsx";
import { API_URLS } from "../utils/apiConfig.ts";
import { Loading } from "../components/loading/index.tsx";
import {
  buildSubscriptionStocksPayload,
  sortWatchList,
  type WatchListSortOrder,
} from "../utils/watchlist.ts";
import { coverageForDisplay, isTelegramChannel } from "../utils/stockCoverage.ts";

const WatchList = () => {
  const history = useHistory();
  const { isMobile } = useMobile();
  const [stockList, setStockList] = useState<any[]>([]);
  const [searhKey, setSearchKey] = useState({
    category: "All",
    searchWord: "",
  });
  const [watchList, setWatchList] = useState<WatchListItem[]>([]);
  // Create activeWatchList from watchList where active === 'Y'
  const activeWatchList = watchList.filter((item: WatchListItem) => item.active === 'Y');
  const [filterKey, setFilterKey] = useState("");
  const [sortOrder, setSortOrder] = useState<WatchListSortOrder>("asc");
  const [hasInactiveStocks, setHasInactiveStocks] = useState(false);
  const hasShownInactiveToast = useRef(false);
  const [themes, setThemes] = useState([]);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const watchListRef = useRef<WatchListItem[]>([]);
  const isSaveDisabledRef = useRef(true);
  const subscriberDetailsRef = useRef<any>(null);
  const watchListStockMap = useRef(new Map());
  const stockMap = useRef(new Map());
  const categoryStockMap = useRef(new Map());
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    fetchSubscriberDetails,
    subscriberDetails,
    setSubscriberDetails,
    isError,
    plans,
    isSubscriberDetailsFetching,
  } = useContext(subscriberContext);
  const getStockLimit = () => {
    const plan = plans.find((p: any) => p.planName === subscriberDetails?.subscribePlan);
    return plan ? plan.noOfStocks : plans[0]?.noOfStocks || 7;
  };

  const addStocksToWatchList = (theme: string, stocks: string[] = []) => {
    const stockLimit = getStockLimit();
    const availableSlots = stockLimit - activeWatchList.length;
    if (availableSlots <= 0) {
      toast.error(
        `You have reached the maximum limit of ${stockLimit} stocks for your ${subscriberDetails.subscribePlan || plans[0]?.planName || "Free"
        } plan`,
        {
          autoClose: 2000,
        }
      );
      return;
    }

    const uniqueStocks = [...new Set(stocks)];
    const stocksToBeAdded = uniqueStocks
      .filter((stock) => !watchListStockMap.current.has(stock))
      .slice(0, availableSlots)
      .filter((scripId) => stockMap.current.get(scripId))
      .map((scripId) => {
        return {
          scripId,
          stockName: stockMap.current.get(scripId).label,
          stockId: stockMap.current.get(scripId).id,
          active: "Y",
        };
      });

    if (stocksToBeAdded.length === 0) {
      toast.info(`Stocks related to ${theme} were already added to watchList`, {
        autoClose: 1000,
      });
      return;
    }

    const nextWatchList = [...stocksToBeAdded, ...watchListRef.current];
    watchListRef.current = nextWatchList;
    setWatchList(nextWatchList);
    stocksToBeAdded.forEach((stock) =>
      watchListStockMap.current.set(stock.scripId, stock)
    );
    toast.success(`Added ${theme} stocks`, {
      autoClose: 1000,
    });
    setIsSaveDisabled(false);
    isSaveDisabledRef.current = false;
    scheduleAutoSave();
  };

  const addStockToWatchList = ({ value, label }: { value: string; label: string }) => {
    if (watchListStockMap.current.has(value)) {
      toast.info(`Stock ${label} was already added to watchList`, {
        autoClose: 1000,
      });
      return;
    }

    const stockLimit = getStockLimit();
    if (activeWatchList.length >= stockLimit) {
      toast.error(
        `You have reached the maximum limit of ${stockLimit} stocks for your ${subscriberDetails.subscribePlan || plans[0]?.planName || "Free"
        } plan`,
        {
          autoClose: 2000,
        }
      );
      return;
    }

    const stockToBeAdded = {
      scripId: value,
      stockName: label,
      stockId: stockMap.current.get(value).id,
      active: "Y",
    };
    watchListStockMap.current.set(value, stockToBeAdded);
    const nextWatchList = [stockToBeAdded, ...watchListRef.current.filter(({ scripId }: WatchListItem) => scripId !== value)];
    watchListRef.current = nextWatchList;
    setWatchList(nextWatchList);
    toast.success(`Added ${label}`, { autoClose: 1000 });
    setIsSaveDisabled(false);
    isSaveDisabledRef.current = false;
    scheduleAutoSave();
  };

  const removeStockFromWatchList = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!e.target.classList.contains("delete")) return;
    const id = e.target.closest("[class*='tile']").id;
    const nextWatchList = watchListRef.current.filter(({ scripId }: { scripId: string }) => scripId !== id);
    watchListRef.current = nextWatchList;
    setWatchList(nextWatchList);
    watchListStockMap.current.delete(id);
    setIsSaveDisabled(false);
    isSaveDisabledRef.current = false;
    scheduleAutoSave();
  };

  const saveWatchList = () => {
    const token = localStorage.getItem("tokn");
    const currentWatchList = watchListRef.current;
    const currentSubscriberDetails = subscriberDetailsRef.current;
    const payload = buildSubscriptionStocksPayload(currentWatchList, watchListStockMap.current);

    const promise = fetch(
      API_URLS.SUBSCRIBER_STOCK(currentSubscriberDetails?.phoneNumber || ""),
      {
        method: "post",
        body: JSON.stringify({
          subscriptionStocks: payload,
        }),
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    ).then(() => {
      setIsSaveDisabled(true);
      isSaveDisabledRef.current = true;
      setSubscriberDetails({
        ...currentSubscriberDetails,
        channelStocks: currentWatchList,
      });
    });

    toast.promise(promise, {
      pending: "Saving…",
      success: "Watchlist auto saved",
      error: "Save failed — try again",
    });
  };

  const scheduleAutoSave = () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(() => {
      if (!isSaveDisabledRef.current) {
        saveWatchList();
      }
    }, 800);
  };

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    watchListRef.current = watchList;
  }, [watchList]);

  useEffect(() => {
    isSaveDisabledRef.current = isSaveDisabled;
  }, [isSaveDisabled]);

  useEffect(() => {
    subscriberDetailsRef.current = subscriberDetails;
  }, [subscriberDetails]);

  useEffect(() => {
    if (isError) {
      history.push("/");
      return;
    }

    if (!subscriberDetails?.phoneNumber)
      return;

    const isPlanUpdated =
      window.location.search && window.location.search.includes("planupdateto");
    if (isPlanUpdated) {
      toast.info(
        `successfully your plan updated to ${subscriberDetails.subscribePlan}`,
        {
          autoClose: 1000,
        }
      );
    }

    const nextWatchList = subscriberDetails.channelStocks || [];
    watchListRef.current = nextWatchList;
    setWatchList(nextWatchList);
    (nextWatchList as WatchListItem[]).forEach((stock: WatchListItem) => {
      if (stock.active === 'Y') {
        watchListStockMap.current.set(stock.scripId, stock);
      }
    });

    for (const stock of nextWatchList as WatchListItem[]) {
      if (stock.active === 'N') {
        setHasInactiveStocks(true);
        break;
      }
    }

    fetch(API_URLS.THEMES)
      .then((resp) => resp.json())
      .then(setThemes);
    fetch(API_URLS.STOCKS_DATA_JSON)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((list) => {
        setStockList(list);
        categoryStockMap.current.set("All", list);
        (list as Array<{ value: string; label: string; id: string; category: string }>).
          forEach(({ value, label, id, category }) => {
            stockMap.current.set(value, { label, id, category });
            if (!category.trim()) {
              return;
            }
            const stocksInCatgeory = categoryStockMap.current.get(category) || [];
            categoryStockMap.current.set(category, [
              ...stocksInCatgeory,
              { value, label, id },
            ]);
          });
      })
      .catch(console.log);
  }, [subscriberDetails, isError]);

  useEffect(() => {
    setStockList(categoryStockMap.current.get(searhKey.category));
  }, [searhKey]);

  // show a one-time toast for inactive stocks (20 seconds)
  useEffect(() => {
    if (hasInactiveStocks && !hasShownInactiveToast.current) {
      toast.info(
        "Few stocks in your watchlist are inactive due to plan expiry—upgrade to reactivate them.",
        { autoClose: 20000 }
      );
      hasShownInactiveToast.current = true;
    }
  }, [hasInactiveStocks]);

  if (isSubscriberDetailsFetching || !subscriberDetails?.phoneNumber || !plans.length) {
    return <Loading />;
  }

  const isWatchListEmpty = watchList.length === 0;
  const filteredWatchList = sortWatchList(
    watchList.filter(
      ({ stockName, stockId = "" }: { stockName: string; stockId?: string }) =>
        stockName.toLowerCase().includes(filterKey.toLowerCase()) ||
        (stockId || "").toLowerCase().includes(filterKey.toLowerCase())
    ),
    sortOrder
  );

  return (
    <Container column minHeight="100vh">
      <Seo title="Watch List | FinQ India" path="/watch-list" noindex />
      <Header active="watch-list" />
      <Container
        width="min(calc(100% - 40px) , 700px)"
        justifyContent="center"
        margin="10px auto"
        column
        gap={"20px"}
        flex="1"
      >
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          transition={Bounce}
          aria-label={"toast"}
        />
        <Typography
          tag="p"
          fontFamily="Neue Montreal"
          fontSize={isMobile ? "28px" : "50px"}
          lineHeight={isMobile ? "43.2px" : "60px"}
          letterSpacing={"-0.01em"}
          color="#202020"
          display="inline"
          textAlign="center"
        >
          Add Stocks to{" "}
          <Typography
            tag="span"
            background="linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)"
            WebkitBackgroundClip="text"
            WebkitTextFillColor="transparent"
          >
            WatchList
          </Typography>
        </Typography>
        {isTelegramChannel(subscriberDetails.channel) && (
          <Typography
            tag="p"
            fontFamily="Neue Montreal"
            fontSize="14px"
            fontWeight={400}
            lineHeight="21px"
            letterSpacing="0.02em"
            color="#828282"
            textAlign="center"
          >
            {coverageForDisplay(
              subscriberDetails.channel,
              subscriberDetails.subscribePlan,
              subscriberDetails.coverage
            ) === "all"
              ? "You hear about every company that files."
              : "You hear about a filing only when it is one of your stocks."}{" "}
            <Link to="/profile" style={{ color: "#5395FF", fontWeight: 500 }}>
              Change in Profile
            </Link>
          </Typography>
        )}
        <Search
          categories={Array.from(categoryStockMap.current.keys())}
          padding={isMobile ? "5px 10px" : "10px"}
          placeholder="Search stocks here"
          onChange={setSearchKey}
        />
        <SearchResults
          results={stockList}
          keyWord={searhKey}
          onSelect={addStockToWatchList}
        />
        <Container column alignItems="center">
          <Typography
            fontFamily="Neue Montreal"
            height={"20px"}
            cursor="pointer"
            fontSize={"12px"}
            fontWeight={"500"}
            color="#828282"
          >
            #TrendingThemes
          </Typography>
          <Container wrap justifyContent="center" gap="10px">
            {themes.slice(0, 4).map(({ name, stocks }: { name: string; stocks: string }, index: number) => (
              <Pill
                index={index}
                onClick={() => addStocksToWatchList(name, stocks.split(","))}
              >
                <Typography
                  color="#fff"
                  cursor="pointer"
                  fontSize={isMobile ? "10px" : "12px"}
                >
                  {name}
                </Typography>
              </Pill>
            ))}
          </Container>
        </Container>

        <Container
          alignItems="center"
          justifyContent={isMobile ? "center" : "space-between"}
          gap="10px"
          wrap
        >
          <Container gap="10px">
            <Typography
              fontFamily="Neue Montreal"
              fontSize={"22px"}
              fontWeight={500}
              lineHeight={"37px"}
              letterSpacing={"0.02em"}
              color="#202020"
            >
              My WatchList
            </Typography>
            <Pill padding="2px" radius="10px">
              <Typography
                fontSize={"14px"}
                fontWeight={500}
                lineHeight={"18.2px"}
                letterSpacing={"0.02em"}
                color="#828282"
              >
                {`${activeWatchList.length}/${getStockLimit()}`}
              </Typography>
            </Pill>
          </Container>
          <Container gap="10px" alignItems="center" wrap>
            {!isWatchListEmpty && (
              <>
                <button
                  type="button"
                  aria-label={`Sort watchlist by company name, ${sortOrder === "asc" ? "A to Z" : "Z to A"}. Activate to reverse order.`}
                  onClick={() =>
                    setSortOrder((order) => (order === "asc" ? "desc" : "asc"))
                  }
                  style={{
                    padding: "10px 14px",
                    borderRadius: "100px",
                    border: "solid 1px #EEE",
                    background: "#FAFAFA",
                    cursor: "pointer",
                    fontFamily: "Neue Montreal, sans-serif",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#828282",
                  }}
                >
                  {sortOrder === "asc" ? "A–Z" : "Z–A"}
                </button>
                <Filter
                  padding="10px"
                  placeholder="Filter stocks watchlist"
                  onChange={setFilterKey}
                />
              </>
            )}
          </Container>
        </Container>
        <Container
          wrap
          gap="10px"
          justifyContent={isMobile ? "center" : ""}
          onClick={removeStockFromWatchList}
          flex="1"
        >
          {isWatchListEmpty ? (
            <Container
              column
              alignItems="center"
              justifyContent="center"
              gap="8px"
              width="100%"
              padding={isMobile ? "24px 16px" : "32px 24px"}
              border="1px dashed #E0E0E0"
              radius="16px"
              background="#FAFAFA"
            >
              <Typography
                fontFamily="Neue Montreal"
                fontSize={isMobile ? "16px" : "18px"}
                fontWeight={500}
                lineHeight="1.4"
                color="#202020"
                textAlign="center"
              >
                Your watchlist is empty
              </Typography>
              <Typography
                fontFamily="Neue Montreal"
                fontSize={isMobile ? "13px" : "14px"}
                fontWeight={400}
                lineHeight="1.5"
                color="#828282"
                textAlign="center"
              >
                {isTelegramChannel(subscriberDetails.channel)
                  ? coverageForDisplay(subscriberDetails.channel, subscriberDetails.subscribePlan, subscriberDetails.coverage) === "all"
                    ? "Add a stock only if you want it on this list."
                    : "Search for a stock above or tap a trending theme to start getting Telegram alerts."
                  : "Search for a stock above or tap a trending theme to start getting WhatsApp alerts."}
              </Typography>
            </Container>
          ) : (
            filteredWatchList.map(({ scripId, stockId = "", stockName, active }: WatchListItem) => (
              <Tile id={scripId} title={stockId} subtitle={stockName} active={active} />
            ))
          )}
        </Container>
        <Container
          justifyContent="center"
          wrap
          margin="0 0 20px 0"
          alignItems="flex-end"
          gap="10px"
        >
          <Typography
            fontFamily="Neue Montreal"
            fontSize={isMobile ? "12px" : "18px"}
            fontWeight={500}
            lineHeight={"37px"}
            letterSpacing={"0.02em"}
            color="#828282"
          >
            Your current plan is{" "}
            <Typography color="#202020" tag="span">
              {subscriberDetails.subscribePlan}
            </Typography>
          </Typography>
          {subscriberDetails.subscribePlan !== "Elite" && (
            <Container
              onClick={() => history.push("/subscription-plans")}
              style={{ cursor: "pointer" }}
            >
              <Typography
                tag="span"
                fontFamily="Neue Montreal"
                fontSize={isMobile ? "12px" : "14px"}
                fontWeight={500}
                lineHeight={"19.6px"}
                color="#fff"
                background="linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)"
                borderRadius={"100px"}
                padding={"8px 20px"}
                display="inline-block"
              >
                Upgrade Now
              </Typography>
            </Container>
          )}
        </Container>
      </Container>
    </Container>
  );
};

export default WatchList;
